import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SerpPreview from 'react-serp-preview';
import { makeStyles } from '@material-ui/core/styles';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { Field } from 'formik';
import { Box, FormHelperText, Typography } from '@material-ui/core';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ChevronRightRounded from '@material-ui/icons/ChevronRightRounded';
import List from '@material-ui/core/List';
import _e from 'common/localeUtil';
import TextInputField from './TextInputField';
import { addSeoSubformOpenCloseClasses, getFullSubdomainURL } from '../../utils';
import TextAreaInputField from './TextAreaInputField';
import PreviewSEODialog from '../dialog/PreviewSEODialog';
import SearchEngineResultDialog from '../dialog/SearchEngineResultDialog';
import SEOPreview from '../SEOPreview';
import ImageSelectorField from './ImageSelectorField';
import EditorLeftPanelContent from '../EditorLeftPanelContent';
import EditorDrawerPanel from '../EditorDrawerPanel';

const useStyles = makeStyles(theme => ({
	listItemStyle: {
		backgroundColor: theme.palette.primary[400],
		// opacity: 0.6,
		borderRadius: 4,
		cursor: 'pointer',
		transition: 'background 0.5s',
		'&:hover': {
			// zIndex: 0,
			backgroundColor: theme.palette.primary[300]
			// opacity: 0.8
		}
	},
	sectionWrapper: {
		fontSize: '1rem'
		// color: theme.palette.text.primary
	},
	seoTitleWrapper: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1)
	},
	sectionSubtitle: {
		marginTop: 5,
		fontSize: '0.8rem'
	},
	searchResultPreview: {
		marginTop: theme.spacing(1),
		backgroundColor: theme.palette.background.paper,
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1),
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
		'&.social-media': {
			padding: 0
		},
		'& >div >div:first-child': {
			fontSize: '0.9rem !important'
		},
		'& >div >div:nth-child(2)': {
			fontSize: '0.7rem !important'
		},
		'& >div >div:nth-child(3)': {
			overflow: 'hidden',
			display: '-webkit-box',
			fontSize: '0.7rem !important',
			lineClamp: 3,
			'-webkit-line-clamp': 3 /* number of lines to show */,
			'-webkit-box-orient': 'vertical'
		}
	},
	searchEngineResult: {
		fontSize: '0.8rem',
		marginBottom: theme.spacing(4),
		'& .searchResultPreview': {
			marginBottom: theme.spacing(1)
		},
		'& .fullPreviewButton': {
			fontSize: '0.8rem',
			fontStyle: 'underline',
			'& a': {
				'& svg': {
					verticalAlign: 'middle'
				}
			}
		}
	},
	form: {
		padding: theme.spacing(2),
		'& .section-title': {
			marginBottom: theme.spacing(1)
		},
		'& .seo-title': {
			marginBottom: theme.spacing(2)
		},
		'& .seo-description': {
			marginBottom: theme.spacing(2)
		},
		'& .seo-focus-keyword .MuiIconButton-root': {
			padding: 0,
			paddingLeft: 4
		},
		'& .seo-focus-keyword': {
			marginBottom: theme.spacing(2)
		},
		'& .input-textfield': {
			color: theme.palette.text.primary
		}
	},
	formTitle: {
		fontWeight: 600,
		fontSize: '0.9rem',
		paddingBottom: theme.spacing(0.5)
	},
	formHelperTextStyle: {
		color: theme.palette.text.primary,
		// color: theme.palette.getContrastText(theme.palette.primary[400]),
		fontSize: '0.7rem'
	}
}));

const SEOSubForm = ({ variant }) => {
	const classes = useStyles();
	const subdomain = useStoreState(state => state.template.subdomain);
	const seo = useStoreState(state => state.template.seo);
	const setSEO = useStoreActions(actions => actions.template.setSEO);
	const saveSEOImageUrl = useStoreActions(actions => actions.template.saveSEOImageUrl);
	const saveFaviconUrl = useStoreActions(actions => actions.template.saveFaviconUrl);

	const fullWebsiteUrl = getFullSubdomainURL(subdomain);
	const { seo_title, seo_description } = seo;
	const [showPreviewSEODialog, setShowPreviewSEODialog] = useState(false);
	const [showSearchEngineResultDialog, setShowSearchEngineResultDialog] = useState(false);
	const [snippetImagePreviewURL, setSnippetImagePreviewURL] = useState(seo.seo_image_url);
	// eslint-disable-next-line no-unused-vars
	const [faviconPreviewURL, setFaviconPreviewURL] = useState(seo.seo_favicon_url);
	const [anchorEl, setAnchorEl] = useState(null);

	useEffect(() => {
		console.log(anchorEl, '- Has changed');
		addSeoSubformOpenCloseClasses(!!anchorEl);
	}, [anchorEl]); // <-- here put the parameter to listen

	const onImageChanged = imageUrl => {
		window.gm_track(`SEO: Image changed`, { imageUrl });
		saveSEOImageUrl(imageUrl);
		setSnippetImagePreviewURL(imageUrl);
	};

	const onFaviconChanged = imageUrl => {
		window.gm_track(`SEO: Favicon changed`, { imageUrl });
		saveFaviconUrl(imageUrl);
		setFaviconPreviewURL(imageUrl);
	};

	const toggleShowSearchEngineResultDialog = () => {
		setShowSearchEngineResultDialog(!showSearchEngineResultDialog);
	};

	const toggleShowPreviewSEODialog = () => {
		window.gm_track(`SEO: Full Preview clicked`);

		setShowPreviewSEODialog(!showPreviewSEODialog);
	};
	const handleOnChange = fieldName => value => {
		// console.log('handleOnChange', e.target.value);
		setSEO({
			...seo,
			[fieldName]: value
		});
		//
	};

	const hideDrawer = () => {
		setAnchorEl(null);
	};

	const togglePopover = () => {
		window.gm_track(`SEO: Customise SEO clicked`);

		setAnchorEl(document.getElementsByClassName('left-panel')[0]);
	};

	const seoContent = (
		<div className={`${classes.form} content-sections-wrapper ${classes.sectionWrapper}`}>
			<Box flex={1}>
				<Typography variant="body1">{_e('customize_seo_desc')}</Typography>
				<Box pt={3} />
				<div className="seo-title">
					<div className={classes.formTitle}>{_e('page_title')}</div>
					<TextInputField
						name="seo.seo_title"
						placeholder={_e('financial_planner')}
						onChangeCallBack={handleOnChange('seo_title')}
					/>
					<FormHelperText classes={{ root: classes.formHelperTextStyle }}>
						{_e('describe_web_page_business')}
					</FormHelperText>
				</div>
				<div className="seo-description">
					<div className={classes.formTitle}>{_e('page_description')}</div>
					<TextAreaInputField
						name="seo.seo_description"
						placeholder=""
						maxLength={400}
						rows={8}
						onChangeCallBack={handleOnChange('seo_description')}
					/>
					<FormHelperText classes={{ root: classes.formHelperTextStyle }}>
						{_e('page_summary_desc')}
					</FormHelperText>
				</div>
				<div className="seo-keywords">
					<div className={classes.formTitle}>{_e('keywords')}</div>
					<TextInputField
						name="seo.seo_focus_keyword"
						placeholder="Financial planner"
						// onChangeCallBack={handleOnChange('seo_focus_keyword')}
					/>
					<FormHelperText classes={{ root: classes.formHelperTextStyle }}>
						{_e('keywords_desc')}
					</FormHelperText>
				</div>
				<Box pt={2} />
				<div className="seo-image">
					<ImageSelectorField
						label={_e('snippet_image')}
						name="seo.seo_image_url"
						showIcon
						showImageEditor
						onValueChanged={onImageChanged}
						maxWidth={1200}
						maxHeight={630}
						cropRatio={1.9035}
					/>
					<FormHelperText classes={{ root: classes.formHelperTextStyle }}>
						<a
							target={`_blank`}
							rel="noopener noreferrer"
							href={`https://goalsmapper.zendesk.com/hc/en-us/articles/900004927023-My-SEO-image-is-not-appearing-correctly-when-i-share-the-website-on-Facebook-`}
						>
							{_e('image_not_appearing')}
						</a>
					</FormHelperText>
				</div>
			</Box>
			<Box pt={2} />
			<div className="seo-favicon">
				<ImageSelectorField
					label={_e('favicon')}
					name="seo.seo_favicon_url"
					avatarSize={96}
					unsplashChangeable={false}
					expectedImageSizeMessage={`Icon should be square and has extension .ico or .png`}
					acceptFiles={`image/png,image/vnd.microsoft.icon,image/x-icon`}
					// showImageEditor
					onValueChanged={onFaviconChanged}
					maxWidth={null}
					// maxHeight={512}
					// cropRatio={1}
				/>
			</div>
		</div>
	);

	const previewMediaUrl =
		snippetImagePreviewURL ||
		'https://goalsmapper-widget.s3-ap-southeast-1.amazonaws.com/default_blank_image.jpg';
	const content = (
		<div className={`${classes.sectionWrapper} ${variant}`}>
			<Field name="seo" as="div">
				<div>
					<div className={`${classes.searchEngineResult}`}>
						<div className={classes.searchResultPreview}>
							<SerpPreview
								title={seo_title}
								width="100%"
								metaDescription={seo_description}
								url={fullWebsiteUrl}
							/>
						</div>
						<div className={`${classes.searchResultPreview} social-media`}>
							<SEOPreview
								header={seo_title}
								description={seo_description}
								media={previewMediaUrl}
								url={fullWebsiteUrl}
							/>
						</div>

						<div className="fullPreviewButton">
							<a
								role="presentation"
								className="section-background-icon iconDialog"
								onClick={toggleShowPreviewSEODialog}
								style={{ textDecoration: 'underline' }}
							>
								{_e('full_preview')}
							</a>
						</div>
					</div>
					<List>
						<ListItem onClick={togglePopover} classes={{ root: classes.listItemStyle }}>
							<ListItemText>{_e('customise_seo')}</ListItemText>
							<ChevronRightRounded />
						</ListItem>
					</List>

					{showPreviewSEODialog && (
						<PreviewSEODialog
							open={showPreviewSEODialog}
							handleClose={toggleShowPreviewSEODialog}
							seoImageUrl={previewMediaUrl}
						/>
					)}
					{showSearchEngineResultDialog && (
						<SearchEngineResultDialog
							open={showSearchEngineResultDialog}
							handleClose={toggleShowSearchEngineResultDialog}
						/>
					)}
				</div>
				<EditorDrawerPanel
					handleClose={hideDrawer}
					content={seoContent}
					anchorEl={anchorEl}
				/>
			</Field>
		</div>
	);
	if (variant === 'full') {
		return (
			<Field name="seo" as="div">
				{seoContent}
			</Field>
		);
	}
	return (
		<EditorLeftPanelContent
			title="SEO"
			subtitle={_e('seo_desc')}
			content={content}
			disabledPadding={variant === 'full'}
		/>
	);
};

SEOSubForm.propTypes = {
	variant: PropTypes.oneOf(['full', 'compact'])
};

SEOSubForm.defaultProps = {
	variant: 'compact'
};

export default SEOSubForm;
